import EventClass from "common/utils/ui/event-class";
import { formatCurrency } from "common/utils/format/number";
import DesignUploadCtaPlpCookieClass from "components/jiffy/design-upload-cta/design-upload-cta-plp/design-upload-cta-plp-cookie-class";
import ProductCardMadeCanvasClass from "../made-canvas/made-canvas-class";

const ACTIVE_SWATCH_CLASS = "js-color-swatch-active";
const ACTIVE_SWATCH_RING_CLASSES = ["ring-2", "ring-offset-1", "ring-blue-600"];

export default class ColorSwatchHoverClass extends EventClass {
  #$target;

  get #$productPreview() {
    return this.#$productCard?.querySelector(".js-product-image");
  }

  get #$productCard() {
    return this.#$target.closest(".js-product-card-v3");
  }

  get #$colorNameDisplay() {
    return this.#$productCard?.querySelector(".js-color-name-display");
  }

  get #$bulkPriceDisplay() {
    return this.#$productCard?.querySelector(".js-bulk-price");
  }

  get #$retailPriceDisplay() {
    return this.#$productCard?.querySelector(".js-retail-price");
  }

  get #$productCardLink() {
    return this.#$productCard?.querySelector(".js-product-card-link");
  }

  get #$bulkPriceTableBody() {
    return this.#$productCard?.querySelector(".js-bulk-price-table-body");
  }

  get #$bulkPriceTableRows() {
    return this.#$bulkPriceTableBody?.querySelectorAll(".js-bulk-price-table-row");
  }

  get #$colorSwatchesContainer() {
    return this.#$productCard?.querySelector(".js-color-swatches-container");
  }

  event(event, $target) {
    super.event(event, $target);

    this.#$target = $target;
    const {
      imgSrc,
      printableArea,
      frontZoomedImageUrl,
      color,
      price,
      tierZeroPrice,
      bulkPrices,
      productUrl
    } = this.#$target.dataset;

    this.#updateProductImage(imgSrc, printableArea, frontZoomedImageUrl);
    this.#updateColorName(color);
    this.#updatePriceDisplay(price, tierZeroPrice);
    this.#updateBulkPriceTable(bulkPrices);
    this.#updateActiveSwatchState();
    this.#updateProductCardUrl(productUrl);
  }

  #updateProductImage(imgSrc, printableArea, frontZoomedImageUrl) {
    if (!this.#$productPreview) return;

    const url = DesignUploadCtaPlpCookieClass.previewUrl;
    if (url && frontZoomedImageUrl && printableArea) {
      this.#$productPreview.src = frontZoomedImageUrl;
      this.#$productPreview.dataset.printableArea = printableArea;
      this.#$productPreview.dataset.frontZoomedImageUrl = frontZoomedImageUrl;
      new ProductCardMadeCanvasClass({ url }).initSingle(this.#$productPreview);
    } else {
      this.#$productPreview.src = imgSrc;
    }
  }

  #updateColorName(colorName) {
    if (!this.#$colorNameDisplay || !colorName) return;
    this.#$colorNameDisplay.textContent = colorName;
  }

  #updatePriceDisplay(price, tierZeroPrice) {
    if (price && this.#$bulkPriceDisplay) {
      this.#$bulkPriceDisplay.textContent = formatCurrency(parseFloat(price));
    }

    if (tierZeroPrice && this.#$retailPriceDisplay) {
      this.#$retailPriceDisplay.textContent = formatCurrency(parseFloat(tierZeroPrice));
    }
  }

  #updateActiveSwatchState() {
    if (!this.#$colorSwatchesContainer) return;

    // Remove active state from all swatches
    const $allSwatches = this.#$colorSwatchesContainer.querySelectorAll(".js-color-swatches-link");
    $allSwatches.forEach($swatch => {
      $swatch.classList.remove(ACTIVE_SWATCH_CLASS, ...ACTIVE_SWATCH_RING_CLASSES);
    });

    // Add active state to the current swatch
    this.#$target.classList.add(ACTIVE_SWATCH_CLASS, ...ACTIVE_SWATCH_RING_CLASSES);
  }

  #updateProductCardUrl(productUrl) {
    if (!productUrl) return;

    // Update the main product card link
    if (this.#$productCardLink) {
      this.#$productCardLink.href = productUrl;
    }

    // Update data-product-url on the product card itself
    if (this.#$productCard) {
      this.#$productCard.dataset.productUrl = productUrl;
      this.#$productCard.dataset.color = this.#$target.dataset.color;
      this.#$productCard.dataset.compositionId = this.#$target.dataset.compositionId;
    }
  }

  #updateBulkPriceTable(bulkPricesJson) {
    if (!bulkPricesJson || !this.#$bulkPriceTableRows) return;

    try {
      const bulkPrices = JSON.parse(bulkPricesJson);
      if (!bulkPrices || typeof bulkPrices !== "object") return;

      // Update each row in the table with the new prices
      this.#$bulkPriceTableRows.forEach($row => {
        const $cells = $row.querySelectorAll(".js-bulk-price-table-cell");
        if ($cells.length >= 2) {
          const quantity = $cells[0].textContent.trim();
          const price = bulkPrices[quantity];
          if (price !== undefined) {
            $cells[1].textContent = formatCurrency(parseFloat(price));
          }
        }
      });
    } catch {
      // Silently fail if JSON parsing fails
    }
  }
}
